import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Scale, 
  Backpack, 
  UserPlus,
  UserCheck 
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const RulesSection = () => {
  const rules = [
    {
      id: "sicurezza",
      title: "Sicurezza sul Campo",
      icon: Shield, // Icona Scudo
      content: [
        "Protezione oculare obbligatoria (occhiali balistici certificati EN166 o superiore)",
        "Protezione facciale consigliata per tutti i partecipanti",
        "Limite potenza: 0,99J misurato con pallini da 0,20g per tutte le repliche",
        "Controllo obbligatorio al cronografo prima di ogni partita",
        "Utilizzo di pallini biodegradabili certificati per rispettare l'ambiente",
      ],
    },
    {
      id: "comportamento",
      title: "Comportamento e Fair Play",
      icon: Scale, // Icona Bilancia (Equità)
      content: [
        "Chiamare sempre i colpi ricevuti alzando la mano e gridando 'COLPITO'",
        "Rispettare gli arbitri, i giudici di gara e le loro decisioni",
        "Vietato qualsiasi tipo di contatto fisico aggressivo",
        "Vietato l'uso di alcol o sostanze prima e durante le partite",
        "Linguaggio rispettoso verso tutti i partecipanti",
      ],
    },
    {
      id: "equipaggiamento",
      title: "Equipaggiamento Consentito",
      icon: Backpack, // Icona Zaino (Equipaggiamento)
      content: [
        "Solo repliche ad aria compressa/gas/co2 con marchio CE",
        "Granate solo a gas/co2, a molla o pirotecniche con marchio CE",
        "Radio consentite su frequenze PMR/LPD autorizzate",
        "Coltelli solo in gomma morbida senza struttura rigida",
        "Abbigliamento: consigliato mimetico o toni neutri",
      ],
    },
    {
      id: "partecipazione",
      title: "Requisiti di Partecipazione",
      icon: UserCheck, // Icona Utente+ (Iscrizione)
      content: [
        "Età minima 16 anni (minori accompagnati da un genitore/tutore)",
        "Tesseramento con assicurazione obbligatorio",
        "Registrazione online entro 48h dall'evento",
        "Quota di partecipazione da versare entro le scadenze previste",
        "Briefing pre-partita obbligatorio per tutti",
      ],
    },
  ];

  return (
    <section id="regolamento" data-testid="rules-section" className="section-tactical py-12 bg-tactical-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          
          {/* Colonna Sinistra - Header */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-tactical-primary" />
              <span className="font-mono text-xs text-tactical-primary tracking-widest uppercase">
                Normative
              </span>
            </div>
            
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-wider text-tactical-text uppercase mb-6">
              REGOLAMENTO <span className="text-tactical-primary">OPERATIVO</span>
            </h2>
            
            <p className="font-body text-tactical-text-muted mb-8 text-sm">
              La sicurezza è la nostra priorità assoluta. Scarica o richiedi il regolamento da leggere attentamente 
              prima di partecipare ai nostri eventi. Il rispetto delle regole garantisce sicurezza e
              divertimento per tutti.
            </p>

            {/* Avviso Importante */}
            <div className="bg-tactical-surface border-l-4 border-tactical-primary p-6 mb-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-tactical-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading font-bold text-lg text-tactical-text uppercase mb-2">
                    Nota Importante
                  </h3>
                  <p className="font-body text-sm text-tactical-text-muted">
                    La violazione del regolamento comporta l'espulsione immediata 
                    dagli eventi senza rimborso.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick summary
            <div className="space-y-3">
              {[
                { icon: CheckCircle, text: "Protezione occhi sempre obbligatoria", color: "text-tactical-success" },
                { icon: CheckCircle, text: "Chiamare sempre i colpi ricevuti", color: "text-tactical-success" },
                { icon: XCircle, text: "Vietato blind shooting", color: "text-tactical-error" }
              ].map((item, i) => (
                <div key={i} className={`flex items-center gap-3 ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                  <span className="font-body text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div> */}
          </div>

          {/* Colonna Destra - Accordion */}
          <div>
            <Accordion type="single" collapsible className="space-y-4">
              {rules.map((rule) => {
                // Definiamo il componente icona con la maiuscola per renderizzarlo
                const Icon = rule.icon; 
                
                return (
                  <AccordionItem
                    key={rule.id}
                    value={rule.id}
                    className="bg-tactical-surface border border-tactical-border data-[state=open]:border-tactical-primary/50"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline group">
                      <div className="flex items-center gap-4">
                        <Icon className="w-5 h-5 text-tactical-primary group-hover:scale-110 transition-transform" />
                        <span className="font-heading font-bold text-left tracking-wider text-tactical-text uppercase group-hover:text-tactical-primary transition-colors">
                          {rule.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <ul className="space-y-3">
                        {rule.content.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 bg-tactical-primary mt-2 flex-shrink-0" />
                            <span className="font-body text-sm text-tactical-text-muted">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};